<script setup lang="ts">
// "Usar na loja" sai do painel (/vip/store não é uma tela /user/*) — sem
// fechar, o overlay do modal fica por cima do destino (mesmo padrão do
// onShortcutClick em ScreenProfile.vue)
const { close } = useUserV2Modal();

// resolveComponent só funciona no setup — no template ele resolve pra
// string e o card vira elemento morto (não navega). Card inteiro (flat =
// Coins/Diamonds/Gems) agora é o link pro desktop também (antes só o botão
// "Usar na loja", escondido em md:hidden, funcionava e só no mobile).
const NuxtLinkComponent = resolveComponent('NuxtLink');

interface Props {
  title: string;
  prefix: string;
  value: string;
  variant?: 'primary' | 'flat';
  icon?: string;
  flag?: string;
  // aba que a loja VIP deve abrir já selecionada (coins → points, diamonds,
  // gems) — sem isso o link caía sempre na aba padrão da loja.
  storeTab?: 'points' | 'diamonds' | 'gems';
  // geometria do ícone decorativo de fundo NO MOBILE — box no tamanho
  // NATIVO do ícone (não o bounding box pós-rotação do Figma: top/right já
  // vêm recalculados preservando o centro de rotação, ver BalancesBar.vue),
  // com a rotação aplicada ao próprio box
  iconTop?: number;
  iconRight?: number;
  iconWidth?: number;
  iconHeight?: number;
  iconRotate?: number;
  // geometria do ícone decorativo NO DESKTOP — mantida como estava antes
  // (valores antigos, um único box compartilhado com o brilho), já que o
  // desktop não passou pelo ajuste pixel-perfect feito pro mobile
  desktopIconTop?: number;
  desktopIconRight?: number;
  desktopIconWidth?: number;
  desktopIconHeight?: number;
  desktopIconRotate?: number;
  // ícone pequeno ao lado do título — no mobile o Figma usa o MESMO ícone
  // (mesmo path, só exportado num canvas menor) duas vezes: pequeno aqui e
  // grande/rotacionado no fundo; cada moeda tem sua proporção própria
  titleIconWidth?: number;
  titleIconHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'flat',
  icon: '',
  flag: '',
  storeTab: undefined,
  iconTop: 0,
  iconRight: -28,
  iconWidth: 100,
  iconHeight: 100,
  iconRotate: 48,
  desktopIconTop: 12,
  desktopIconRight: -28,
  desktopIconWidth: 100,
  desktopIconHeight: 100,
  desktopIconRotate: 48,
  titleIconWidth: 20,
  titleIconHeight: 20,
});

const storeLink = computed(() =>
  localeLink(
    props.storeTab ? `/vip/store?tab=${props.storeTab}` : '/vip/store',
  ),
);
</script>

<template>
  <component
    :is="props.variant === 'flat' ? NuxtLinkComponent : 'div'"
    class="relative flex w-full flex-col gap-[10px] overflow-clip rounded-[16px] p-[16px] md:gap-[4px]"
    :class="[
      props.variant === 'primary' ? 'bg-v2-primary-700' : 'bg-v2-default/40',
      props.variant === 'flat' ? 'transition hover:brightness-110' : '',
    ]"
    :to="props.variant === 'flat' ? storeLink : undefined"
    @click="props.variant === 'flat' && close()"
  >
    <!-- MOBILE: brilho de fundo (Figma "Ellipse 56", mesma geometria nas 3
         moedas) separado do ícone nítido (posição/tamanho/rotação próprios
         por moeda) — geometria validada contra o Figma -->
    <img
      v-if="props.variant === 'flat' && props.icon"
      aria-hidden="true"
      class="pointer-events-none absolute right-[-48px] top-[84px] size-[126px] scale-125 object-contain opacity-70 blur-2xl md:hidden"
      :src="props.icon"
    />

    <div
      v-if="props.variant === 'flat' && props.icon"
      class="pointer-events-none absolute md:hidden"
      :style="{
        top: `${props.iconTop}px`,
        right: `${props.iconRight}px`,
        width: `${props.iconWidth}px`,
        height: `${props.iconHeight}px`,
        transform: `rotate(${props.iconRotate}deg)`,
      }"
    >
      <img
        alt=""
        class="relative size-full object-contain"
        :src="props.icon"
      />
    </div>

    <!-- DESKTOP: mantido como estava antes do ajuste pixel-perfect do mobile
         (brilho + ícone no mesmo box) -->
    <div
      v-if="props.variant === 'flat' && props.icon"
      class="pointer-events-none absolute hidden md:block"
      :style="{
        top: `${props.desktopIconTop}px`,
        right: `${props.desktopIconRight}px`,
        width: `${props.desktopIconWidth}px`,
        height: `${props.desktopIconHeight}px`,
        transform: `rotate(${props.desktopIconRotate}deg)`,
      }"
    >
      <img
        aria-hidden="true"
        class="absolute inset-0 size-full scale-125 object-contain opacity-70 blur-2xl"
        :src="props.icon"
      />
      <img
        alt=""
        class="relative size-full object-contain"
        :src="props.icon"
      />
    </div>

    <div class="relative flex w-full items-center justify-between">
      <p class="text-[14px] font-medium leading-[20px] text-white">
        {{ props.title }}
      </p>
      <img
        v-if="props.flag"
        alt=""
        class="h-[20px] w-[29px] rounded-[5px] object-cover"
        :src="props.flag"
      />
      <img
        v-if="props.variant === 'flat' && props.icon"
        alt=""
        class="shrink-0 object-contain md:hidden"
        :src="props.icon"
        :style="{
          width: `${props.titleIconWidth}px`,
          height: `${props.titleIconHeight}px`,
        }"
      />
    </div>

    <p class="relative text-[20px] font-bold leading-[28px] text-white">
      <span class="text-white/30">{{ props.prefix }}</span>
      {{ props.value }}
    </p>

    <UserV2WalletActionButtons
      v-if="props.variant === 'primary'"
      class="relative md:hidden"
      size="card"
    />

    <!-- Card inteiro já é o link (ver <component> acima) — isso aqui é só o
         rótulo visual do mobile, sem navegação própria (senão vira <a>
         dentro de <a>). -->
    <span
      v-if="props.variant === 'flat'"
      class="border-solid relative flex h-[32px] w-fit items-center justify-center gap-[8px] rounded-[8px] border-2 border-white px-[12px] text-[12px] leading-[16px] text-white md:hidden"
    >
      Usar na loja
      <UserV2Icon name="chevron-right" />
    </span>
  </component>
</template>
